import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Search, Plus, Pin, PinOff, Folder, Pencil, Copy, Trash } from 'lucide-react';

const EMPTY_FILTERS = [];

export default function FilterDropdown({
    filters = EMPTY_FILTERS,
    activeFilterId,
    onSelect,
    onCreate,
    onTogglePin,
    onRename,
    onDuplicate,
    onDelete,
    activeFilterName
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [isActionsOpen, setIsActionsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const dropdownRef = useRef(null);
    const actionsRef = useRef(null);
    const inputRef = useRef(null);

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
            if (actionsRef.current && !actionsRef.current.contains(event.target)) {
                setIsActionsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Focus input on open
    useEffect(() => {
        if (isOpen && inputRef.current) {
            const id = setTimeout(() => inputRef.current.focus(), 50);
            return () => clearTimeout(id);
        }
    }, [isOpen]);

    // Filter list
    const filteredList = filters.filter(f =>
        f.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const pinnedFilters = filteredList.filter(f => f.is_pinned);
    const otherFilters = filteredList.filter(f => !f.is_pinned);

    // Helper to render an item
    const renderItem = (filter) => {
        const isSelected = filter.id === activeFilterId;
        return (
            <div
                key={filter.id}
                role="button"
                tabIndex={0}
                onClick={() => {
                    onSelect(filter.id);
                    setIsOpen(false);
                }}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onSelect(filter.id);
                        setIsOpen(false);
                    }
                }}
                className={`group flex items-center justify-between px-3 py-2 text-sm rounded-md cursor-pointer transition-colors ${isSelected ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50 text-gray-700'}`}
            >
                <div className="flex items-center gap-2 min-w-0">
                    <Folder className={`w-4 h-4 flex-shrink-0 ${isSelected ? 'text-blue-500' : 'text-gray-400'}`} />
                    <span className="truncate font-medium">{filter.name}</span>
                    {isSelected && <span className="bg-blue-100 text-blue-700 text-[10px] px-1.5 py-0.5 rounded-full font-semibold">current</span>}
                </div>

                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        onTogglePin(filter.id, !filter.is_pinned);
                    }}
                    className={`p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity hover:bg-gray-200 ${filter.is_pinned ? 'text-blue-600 opacity-100' : 'text-gray-400'}`}
                    title={filter.is_pinned ? "Unpin filter" : "Pin filter"}
                >
                    {filter.is_pinned ? (
                        <PinOff className="w-3.5 h-3.5 fill-current" />
                    ) : (
                        <Pin className="w-3.5 h-3.5" />
                    )}
                </button>
            </div>
        );
    };

    return (
        <div className="flex items-center gap-2 text-xl font-semibold text-gray-800">
            {/* Filter Selection Dropdown */}
            <div className="relative inline-block text-left" ref={dropdownRef}>
                <button
                    type="button"
                    onClick={() => {
                        setIsOpen(!isOpen);
                        setIsActionsOpen(false);
                    }}
                    className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg text-sm font-medium transition-colors cursor-pointer group"
                >
                    <Folder className="w-4 h-4" />
                    Filters
                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''} group-hover:text-gray-600`} />
                </button>

                {isOpen && (
                    <div className="absolute top-full left-0 mt-2 w-72 bg-white rounded-xl shadow-2xl border border-gray-100 ring-1 ring-black ring-opacity-5 z-50 animate-in fade-in zoom-in-95 duration-100 origin-top-left flex flex-col max-h-[500px]">
                        {/* Header: Create New */}
                        <div className="p-2 border-b border-gray-100 flex-shrink-0">
                            <button
                                type="button"
                                onClick={() => {
                                    onCreate();
                                    setIsOpen(false);
                                }}
                                className="w-full flex items-center justify-center gap-2 bg-blue-50 hover:bg-blue-100 text-blue-600 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                            >
                                <Plus className="w-4 h-4" />
                                Create new filter
                            </button>
                        </div>

                        {/* Search Bar */}
                        <div className="p-2 border-b border-gray-100 flex-shrink-0">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                    ref={inputRef}
                                    type="text"
                                    aria-label="Search filters"
                                    placeholder="Search..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 placeholder-gray-400"
                                />
                            </div>
                        </div>

                        {/* Scrollable List */}
                        <div className="overflow-y-auto flex-1 p-1 custom-scrollbar">
                            {/* Pinned Section */}
                            {pinnedFilters.length > 0 && (
                                <div className="mb-2">
                                    <div className="px-3 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                                        Pinned
                                    </div>
                                    <div className="space-y-0.5">
                                        {pinnedFilters.map(renderItem)}
                                    </div>
                                    <div className="border-b border-gray-100 my-2 mx-2"></div>
                                </div>
                            )}

                            {/* All Filters Section */}
                            <div>
                                <div className="px-3 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                                    All filters
                                </div>
                                <div className="space-y-0.5">
                                    {otherFilters.length > 0 ? (
                                        otherFilters.map(renderItem)
                                    ) : (
                                        <div className="px-3 py-4 text-center text-sm text-gray-400">
                                            No filters found
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <span className="text-gray-300 font-light">/</span>

            {/* Current Filter Actions Dropdown */}
            {activeFilterName && (
                <div className="relative inline-block text-left" ref={actionsRef}>
                    <button
                        type="button"
                        onClick={() => {
                            setIsActionsOpen(!isActionsOpen);
                            setIsOpen(false);
                        }}
                        className="flex items-center gap-2 hover:bg-gray-50 px-2 py-1 rounded-lg transition-colors group cursor-pointer text-sm font-medium"
                    >
                        <span className="group-hover:text-blue-600 transition-colors">
                            {activeFilterName}
                        </span>
                        <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isActionsOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {isActionsOpen && (
                        <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-100 ring-1 ring-black ring-opacity-5 z-50 animate-in fade-in zoom-in-95 duration-100 origin-top-left py-1">

                            <button
                                type="button"
                                onClick={() => {
                                    onRename();
                                    setIsActionsOpen(false);
                                }}
                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                            >
                                <Pencil className="w-4 h-4 text-gray-500" />
                                Rename filter
                            </button>

                            <button
                                type="button"
                                onClick={() => {
                                    onDuplicate();
                                    setIsActionsOpen(false);
                                }}
                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                            >
                                <Copy className="w-4 h-4 text-gray-500" />
                                Duplicate
                            </button>

                            <div className="my-1 border-t border-gray-100"></div>

                            <button
                                type="button"
                                onClick={() => {
                                    onDelete();
                                    setIsActionsOpen(false);
                                }}
                                className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                            >
                                <Trash className="w-4 h-4" />
                                Delete filter
                            </button>
                        </div>
                    )}
                </div>
            )}
            {!activeFilterName && (
                <div className="text-gray-900 px-2 py-1">Select Filter</div>
            )}
        </div>
    );
}
